// @ts-nocheck
import { FC, useEffect, useMemo } from 'react';
import Link from 'next/link';
import ActivityItem from 'components/Activity';
import { Activity } from 'reducers/activities/types';
import Text from 'components/core/Text';
import Loader from 'components/Loader';
import sortPrograms from 'utils/sortPrograms';

import {
  EmptyState,
  EmptyDescription,
  NextUpCta,
  NextUpInfo,
  NextUpLabel,
  NextUpTitle,
  NextUpArrow,
  SectionHeader,
  SectionLink,
  ActivityGrid,
  ActivitiesStats,
} from './styled';

interface Props {
  /** Activity list
   * @default []
   */
  activities: Activity[];

  /** Is activity list loading flag
   * @default false
   */
  loading: boolean;

  /** Total count of programs in activities
   * @default 0
   * */
  totalPrograms?: number;

  /** Total count of sessions in activities
   * @default 0
   * */
  totalSessions?: number;

  /** Show activities in two columns
   * @default false
   * */
  withColumns?: boolean;

  /** Callback for load activities
   *  @default () => {}
   */
  requestFetchActivities?: (data: { search?: string }) => void;

  /** User's programs (used to surface the next-up session in the empty state)
   * @default []
   */
  programs?: any[];
}

const ActivityWidget: FC<Props> = ({
  activities = [],
  loading = true,
  totalPrograms = 0,
  totalSessions = 0,
  withColumns = false,
  requestFetchActivities = () => {},
  programs = [],
}) => {
  const featuredProgram = useMemo(() => {
    return programs.length ? sortPrograms([...programs])[0] : null;
  }, [programs]);

  const nextSessionUrl =
    featuredProgram?.nextSession?.id && featuredProgram?.id
      ? `/programs/${featuredProgram.id}/sessions/${featuredProgram.nextSession.id}/start?trackSessionId=${featuredProgram.nextTrackSessionId}`
      : null;
  useEffect(() => {
    requestFetchActivities?.({
      search: '',
    });
  }, [requestFetchActivities]);

  const renderedContent = useMemo(() => {
    const activitiesList = activities
      ?.filter((activity) => activity.type === 'workout' || activity.type === 'external')
      ?.slice(0, 4);

    return activitiesList.length > 0 ? (
      <ActivityGrid withColumns={withColumns}>
        {activitiesList.map((item) => (
          <ActivityItem
            key={item.id}
            id={item.id}
            type={item.type}
            session={item.session}
            program={item.program}
            trackSessionId={item.trackSessionId}
            ease={item.ease}
            quality={item.quality}
            density={item.density}
            exercisesCount={item.exercisesCount}
            timeLength={item.timeLength}
            workoutType={item.workoutType}
            rating={item.rating}
            tags={item.tags}
            ponder={item.ponder}
            picture={item.picture}
          />
        ))}
      </ActivityGrid>
    ) : (
      <EmptyState>
        <Text variant="displayX" color="black" transform="uppercase" isCondensed align="center">
          No sessions yet
        </Text>
        <EmptyDescription>
          <Text variant="text" color="grayLight" align="center">
            Complete your first session and a summary of your activity will appear here.
          </Text>
        </EmptyDescription>
        {nextSessionUrl && featuredProgram?.nextSession && (
          <Link href={nextSessionUrl} passHref legacyBehavior>
            <NextUpCta>
              <NextUpInfo>
                <NextUpLabel>Next up</NextUpLabel>
                <NextUpTitle>{featuredProgram.nextSession.name}</NextUpTitle>
              </NextUpInfo>
              <NextUpArrow aria-hidden>→</NextUpArrow>
            </NextUpCta>
          </Link>
        )}
      </EmptyState>
    );
  }, [activities, withColumns, nextSessionUrl, featuredProgram]);

  return (
    <div>
      <SectionHeader>
        <div>
          <Text variant="sectionHeader" transform="uppercase" mb={1}>
            Recent activity
          </Text>
          <ActivitiesStats>
            <Text variant="captionSmall">{totalPrograms} programs</Text>
            <Text variant="captionSmall">{totalSessions} sessions</Text>
          </ActivitiesStats>
        </div>
        {!!activities.length && <SectionLink href="/activity">More</SectionLink>}
      </SectionHeader>
      {loading ? <Loader /> : renderedContent}
    </div>
  );
};

export default ActivityWidget;
